import type { Scene } from './types';
import { ensureBackdrop, drawEffects, drawOneBlast, drawSmokeScreen, drawOneCasing, drawOneMuzzlePuff, drawDamages, drawFlashbangs, flashbangWash } from './effects';
import { drawShadow, drawFighter, drawFighterWeapon, drawFighterLabel, actionBadge, fighterBadge } from './fighters';
import { drawHpBar, drawKillFeed } from './hud';
import { drawRing, RING_GREEN, RING_RED, RING_SMOKE } from './markers';
import { aimTargetOf, type BattleEngineOpts, type PlaybackState } from './playbackState';

/** The world's on-screen rect once letterboxed into the canvas: horizontal extent + offset +
 *  scale. The HUD (top HP bar) spans the field, not the full canvas, so it lines up with the
 *  playfield rather than the gutters. */
export interface FieldLayout { fieldX: number; fieldW: number; offY: number; scale: number; }

export const computeField = (cw: number, ch: number, safeBottom: number, W: number, H: number): FieldLayout => {
  // fit the world into the band ABOVE the action bar (safeBottom) so the bottom row of a 5v5
  // never hides under the controls, then centre within that band.
  const availH = Math.max(1, ch - safeBottom);
  const scale = Math.min(cw / W, availH / H);
  const fieldW = W * scale;
  const fieldX = (cw - fieldW) / 2;
  const offY = (availH - H * scale) / 2;
  return { fieldX, fieldW, offY, scale };
};

/** Draw one frame: clear, letterbox the world, then paint bottom→top — ground layers (shadows,
 *  you/target decals), the y-sorted world pass (fighters/smoke/brass by depth), top effects
 *  (blasts, flashbangs, labels, tracers) and the screen-space HUD. */
export const renderScene = (
  sc: Scene, opts: BattleEngineOpts, n: number,
  cw: number, ch: number, dpr: number, safeTop: number,
  field: FieldLayout, replaying: boolean, state: PlaybackState,
): void => {
  const { ctx } = sc;
  // static backdrop (blurred gutters + darken + sharp map) — composited once per resize/map,
  // blitted here 1:1 at device pixels. Opaque + full-screen, so it doubles as the frame clear.
  const backdrop = ensureBackdrop(sc, cw, ch, dpr, field.fieldX, field.offY, field.scale);
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  if (backdrop) ctx.drawImage(backdrop, 0, 0);
  else ctx.clearRect(0, 0, cw * dpr, ch * dpr); // getContext failed — at least clear

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.save();
  ctx.translate(field.fieldX, field.offY);
  ctx.scale(field.scale, field.scale);
  // (the sharp map is baked into the backdrop above — no per-frame map draw here)

  // a read-only replay (spectator watching a live match, or a history playback) has no viewer
  // fighter: draw NO selection chrome at all — no "you" green ring, no target ring, no "you"
  // name highlight. Only an interactive local player sees those. `opts.replay` is the mode flag
  // (not the per-round `replaying` animation phase, which still drives the barrels below).
  const youId = opts.replay ? null : opts.getYouId();
  // the target ring follows the SAME enemy as your barrel (aimTargetOf) — decision phase: your
  // ring pick; replay: the target you actually engaged this round. NO live-pick fallback during
  // replay: that made the ring show your NEXT-round pick while the body faced this round's target.
  const target = opts.replay ? null : aimTargetOf(youId, youId, replaying, state, opts);
  const ordered = [...sc.fighters].sort((a, b) => a.y - b.y);
  const youFighter = youId ? sc.byId[youId] : null;
  const youAlive = !youFighter?.dead;
  const youTeam = youFighter?.team;
  // YOUR name reflects your just-committed action immediately (before the round resolves);
  // once events arrive it agrees with the event-derived badge every fighter gets.
  const youBadge = youFighter ? actionBadge(sc, opts.getChosenAction()) : null;

  for (const f of ordered) drawShadow(sc, f, n);
  // your own ring
  if (youFighter && !youFighter.dead) drawRing(sc, youFighter, n, RING_GREEN);
  // target ring — STAYS on this round's target for the WHOLE replay, even after it dies
  // mid-round (drawn on the corpse), so it never blinks off and re-appears on the next
  // round's target (which read as the marker "jumping" to another enemy). In the decision
  // phase it only shows on a live target. A teammate target (smoke) gets the neutral grey ring.
  if (target && youAlive && target !== youId) {
    const tf = sc.byId[target];
    if (tf && (!tf.dead || replaying)) {
      drawRing(sc, tf, n, tf.team === youTeam ? RING_SMOKE : RING_RED);
    }
  }
  // WORLD PASS — painter's algorithm by ground y: fighters (body + weapon, atomic), smoke
  // clouds, spent brass and barrel wisps interleave by depth, so a LOWER (closer) fighter
  // draws over an upper fighter's smoke/shells instead of every cloud/shell sitting above
  // every body. Push order breaks the ties (stable sort): a fighter first, then the smoke
  // covering it, then its shells — the cloud hides its own fighter, shells lie at its feet.
  const world: { y: number; draw: () => void }[] = [];
  for (const f of ordered) world.push({ y: f.y, draw: () => { drawFighter(sc, f, n); drawFighterWeapon(sc, f, n, state); } });
  for (const [id, s] of state.smokeScreens) world.push({ y: s.y, draw: () => drawSmokeScreen(sc, id, s, n, state) });
  for (const bl of sc.blasts) world.push({ y: bl.groundY, draw: () => drawOneBlast(sc, bl, n) });
  for (const c of sc.casings) world.push({ y: c.groundY, draw: () => drawOneCasing(sc, c, n) });
  for (const p of sc.muzzlePuffs) world.push({ y: p.oy, draw: () => drawOneMuzzlePuff(sc, p, n) });
  world.sort((a, b) => a.y - b.y);
  for (const it of world) it.draw();
  drawFlashbangs(sc, n); // white burst rings over the blinded cluster
  for (const f of ordered) {
    // every fighter shows their round action; your own name prefers the instant local badge
    const badge = (f.id === youId && youBadge) ? youBadge : fighterBadge(sc, f);
    drawFighterLabel(sc, f, f.id === youId, badge);
  }
  drawEffects(sc, n);
  drawDamages(sc, n);
  drawKillFeed(sc, n);
  ctx.restore();

  // full-screen white-out the instant a flashbang pops (POV blind), fading fast
  const flashWash = flashbangWash(sc, n);
  if (flashWash > 0) {
    ctx.save();
    ctx.globalAlpha = flashWash;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, cw, ch);
    ctx.restore();
  }

  drawHpBar(sc, field.fieldX, field.fieldW, safeTop);
};
